import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionInfo } from '@ana/contracts';
import { ApiError, NetworkError, api, onUnauthorized, setCsrfToken } from '../api/client.ts';
import { clearUserData } from '../offline/clear.ts';
import { offlineCan, parseCachedIdentity, toCachedIdentity } from './offline-identity.ts';

interface SessionState {
  session: SessionInfo | null;
  loading: boolean;
  /** True when the server could not be reached but a cached identity exists (offline drafting). */
  offlineIdentity: boolean;
  unitId: string | null;
  setUnitId: (id: string) => void;
  can: (permission: string) => boolean;
  refresh: () => Promise<void>;
  apply: (s: SessionInfo) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);
const UNIT_KEY = 'ana.unitId';
const IDENTITY_KEY = 'ana.lastIdentity';

/** Stored identity still valid for offline drafting (offline-identity.ts); an expired one is removed. */
function readIdentity() {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY);
    const cached = parseCachedIdentity(raw, new Date());
    if (raw && !cached) localStorage.removeItem(IDENTITY_KEY);
    return cached;
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [offlineIdentity, setOfflineIdentity] = useState(false);
  const [unitId, setUnit] = useState<string | null>(() => {
    try {
      return localStorage.getItem(UNIT_KEY);
    } catch {
      return null;
    }
  });

  const apply = useCallback((s: SessionInfo) => {
    setCsrfToken(s.csrfToken);
    setSession(s);
    setOfflineIdentity(false);
    if (s.gates.length === 0) {
      try {
        // Only units where the user may draft are kept; with none, no identity stays on the device.
        const identity = toCachedIdentity(s, new Date());
        if (identity) localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
        else localStorage.removeItem(IDENTITY_KEY);
      } catch {
        /* storage unavailable: offline drafting simply will not be offered */
      }
    }
    setUnit((current) => {
      const valid = s.units.find((u) => u.id === current);
      return valid ? current : (s.units[0]?.id ?? null);
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await api<SessionInfo>('/auth/me'));
    } catch (e) {
      if (e instanceof NetworkError) {
        // Offline start: allow local drafting with the last known identity, confirmed by the server less than
        // 72 h ago (D-13). Server actions stay disabled.
        const cached = readIdentity();
        if (cached) {
          setOfflineIdentity(true);
          setSession({
            user: {
              id: cached.userId,
              email: '',
              displayName: cached.displayName,
              mfaEnabled: false,
              isDemo: false,
            },
            csrfToken: '',
            gates: [],
            units: cached.units,
            environment: 'OFFLINE',
            serverTime: cached.at,
          });
          setUnit((current) =>
            cached.units.some((u) => u.id === current) ? current : (cached.units[0]?.id ?? null),
          );
        }
      } else if (e instanceof ApiError && e.status === 401) {
        setSession(null);
      }
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
    return onUnauthorized(() => setSession(null));
  }, [refresh]);

  // Started offline with the cached identity: once the network returns, confirm the real session
  // (sync only runs with a live, server-validated session).
  useEffect(() => {
    if (!offlineIdentity) return;
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [offlineIdentity, refresh]);

  const logout = useCallback(async () => {
    const uid = session?.user.id;
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      /* already expired */
    }
    // Local drafts of this user are removed on logout (threat model T11).
    if (uid) await clearUserData(uid);
    try {
      localStorage.removeItem(IDENTITY_KEY);
    } catch {
      /* ignore */
    }
    setSession(null);
  }, [session]);

  const value = useMemo<SessionState>(() => {
    const unit = session?.units.find((u) => u.id === unitId);
    return {
      session,
      loading,
      offlineIdentity,
      unitId: unit?.id ?? null,
      setUnitId: (id) => {
        setUnit(id);
        try {
          localStorage.setItem(UNIT_KEY, id);
        } catch {
          /* ignore */
        }
      },
      can: (p) => (offlineIdentity ? offlineCan(unit, p) : Boolean(unit?.permissions.includes(p))),
      refresh,
      apply,
      logout,
    };
  }, [session, loading, offlineIdentity, unitId, refresh, apply, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider ausente');
  return v;
}
